import { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link, useSearch } from "wouter";
import {
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  User,
  Scissors,
  CheckCircle,
  ArrowLeft,
  Loader2,
  Phone,
  Mail,
  MessageCircle,
  CalendarCheck,
  CalendarPlus,
  CalendarX,
  AlertTriangle,
  Megaphone,
  Tag,
  ChevronDown,
  MapPin,
  Check,
  CreditCard,
  Download,
} from "lucide-react";
import { format, addDays, startOfWeek, isSameDay, isToday, isPast } from "date-fns";
import { ptBR } from "date-fns/locale";
import { BeautyChatBot } from "@/components/BeautyChatBot";

type Step = "service" | "branch" | "professional" | "datetime" | "client" | "confirm" | "success";
const BOOKING_PROGRESS_KEY = "booking-public-progress";

type ConfirmedBookingCalendarData = {
  serviceNames: string[];
  profName: string;
  date: string;
  totalDuration: number;
};

function escapeIcsText(value: string) {
  return value.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}

export function downloadBookingCalendar(data: ConfirmedBookingCalendarData) {
  const match = data.date.match(/(\d{2})\/(\d{2})\/(\d{4}) às (\d{2}):(\d{2})/);
  if (!match) return false;
  const [, day, month, year, hour, minute] = match;
  const start = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), 0);
  const end = new Date(start.getTime() + Math.max(data.totalDuration, 30) * 60_000);
  const toIcsDate = (value: Date) => `${value.getFullYear()}${String(value.getMonth() + 1).padStart(2, "0")}${String(value.getDate()).padStart(2, "0")}T${String(value.getHours()).padStart(2, "0")}${String(value.getMinutes()).padStart(2, "0")}00`;
  const services = data.serviceNames.join(", ");
  const file = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Beleza e Bronze//Reserva//PT-BR",
    "BEGIN:VEVENT",
    `UID:beleza-e-bronze-${Date.now()}@booking`,
    `DTSTAMP:${toIcsDate(new Date())}`,
    `DTSTART:${toIcsDate(start)}`,
    `DTEND:${toIcsDate(end)}`,
    `SUMMARY:${escapeIcsText(`Reserva: ${services}`)}`,
    `DESCRIPTION:${escapeIcsText(`Profissional: ${data.profName}\nServiços: ${services}`)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([file], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `reserva-beleza-e-bronze-${year}${month}${day}.ics`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return true;
}

export default function BookingPage() {
  const { user, isAuthenticated } = useAuth();
  const search = useSearch();
  const params = new URLSearchParams(search);
  // Estabilizar salonId e branchId com useMemo para evitar re-fetches infinitos
  const salonId = useMemo(() => parseInt(params.get("salon") ?? "0", 10) || 0, [search]);
  const branchId = useMemo(() => parseInt(params.get("branchId") ?? "0", 10) || null, [search]);
  const branchName = useMemo(() => params.get("branchName") ? decodeURIComponent(params.get("branchName")!) : null, [search]);
  const depositSessionId = useMemo(() => params.get("deposit_session_id") ?? "", [search]);
  const depositCancelled = useMemo(() => params.get("deposit_cancelled") === "1", [search]);

  const [step, setStep] = useState<Step>(() => {
    const saved = sessionStorage.getItem(`${BOOKING_PROGRESS_KEY}-${salonId}`) as Step | null;
    return ["service", "branch", "professional", "datetime", "client", "confirm"].includes(saved ?? "") ? saved! : "service";
  });
  // Multi-seleção de serviços
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const [selectedProfessional, setSelectedProfessional] = useState<number | null>(null);
  // Filial selecionada pelo cliente no step de branch (sobrescreve branchId da URL)
  const [selectedBranchId, setSelectedBranchId] = useState<number | null>(null);
  const [selectedBranchName, setSelectedBranchName] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // selectedSlot agora é uma string "HH:mm" para evitar drift de fuso horário
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));

  // Dados do cliente
  const [clientName, setClientName] = useState(() => user?.name ?? "");
  const [clientPhone, setClientPhone] = useState(() => user?.phone ?? "");
  const [clientEmail, setClientEmail] = useState(() => user?.email ?? "");
  const [acceptedBookingPolicy, setAcceptedBookingPolicy] = useState(false);

  // Dados do agendamento confirmado (para tela de sucesso)
  const [confirmedData, setConfirmedData] = useState<{
    serviceNames: string[];
    profName: string;
    date: string;
    phone: string;
    salonPhone?: string;
    totalPrice: number;
    totalDuration: number;
    advancePaymentAmount?: number;
    advancePaymentPercent?: number;
  } | null>(null);

  // Progresso de criação de múltiplos agendamentos
  const [creatingProgress, setCreatingProgress] = useState(0);

  useEffect(() => {
    if (step === "success") {
      sessionStorage.removeItem(`${BOOKING_PROGRESS_KEY}-${salonId}`);
      return;
    }
    sessionStorage.setItem(`${BOOKING_PROGRESS_KEY}-${salonId}`, step);
  }, [salonId, step]);

  // Filial efetiva: da URL ou selecionada pelo cliente
  const effectiveBranchId = selectedBranchId ?? branchId;
  const effectiveBranchName = selectedBranchName ?? branchName;

  // Primeiro serviço selecionado (usado para buscar filiais e slots)
  const primaryServiceId = selectedServices[0] ?? null;

  // Stable query inputs — objetos literais inline causam re-fetches infinitos
  const servicesInput = useMemo(() => ({ salonId, ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}) }), [salonId, effectiveBranchId]);
  const profsInput = useMemo(
    () => ({ salonId, serviceIds: selectedServices, ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}) }),
    [salonId, selectedServices, effectiveBranchId]
  );
  // Buscar filiais disponíveis para o serviço principal selecionado
  const serviceBranchesInput = useMemo(() => ({ serviceId: primaryServiceId ?? 0, salonId }), [primaryServiceId, salonId]);
  const { data: serviceBranches } = (trpc as any).branches.getBranchesForService.useQuery(
    serviceBranchesInput,
    { enabled: !!primaryServiceId && salonId > 0 }
  );
  const salonInfoInput = useMemo(() => ({ salonId }), [salonId]);
  const publicScheduleInput = useMemo(() => ({ salonId }), [salonId]);

  const { data: services, isLoading: servicesLoading } = trpc.services.list.useQuery(
    servicesInput,
    { enabled: salonId > 0 }
  );
  const { data: professionals, isLoading: profLoading } = (trpc as any).professionals.listByServices.useQuery(
    profsInput,
    { enabled: salonId > 0 && selectedServices.length > 0 }
  );

  // Duração total dos serviços selecionados (para buscar slots com duração correta)
  const totalDurationMinutes = useMemo(() => {
    if (!services) return 0;
    return selectedServices.reduce((acc, id) => {
      const s = services.find((sv) => sv.id === id);
      return acc + (s?.durationMinutes ?? 0);
    }, 0);
  }, [services, selectedServices]);

  const totalPrice = useMemo(() => {
    if (!services) return 0;
    return selectedServices.reduce((acc, id) => {
      const s = services.find((sv) => sv.id === id);
      return acc + Number(s?.price ?? 0);
    }, 0);
  }, [services, selectedServices]);

  const slotsInput = useMemo(
    () => ({
      professionalId: selectedProfessional ?? 0,
      serviceId: primaryServiceId ?? 0,
      date: selectedDate,
      // Enviar data local como string YYYY-MM-DD para evitar drift de fuso horário no servidor
      dateStr: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
      // Enviar dia da semana local (0=Dom, 1=Seg, ..., 6=Sáb)
      localDayOfWeek: selectedDate.getDay(),
      // Filial selecionada para restringir slots ao horário da filial
      ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
    }),
    [selectedProfessional, primaryServiceId, selectedDate, effectiveBranchId]
  );

  const { data: slots, isLoading: slotsLoading } = trpc.appointments.getAvailableSlots.useQuery(
    slotsInput,
    { enabled: !!selectedProfessional && !!primaryServiceId }
  );

  const { data: salonInfo } = trpc.salon.getPublicInfo.useQuery(
    salonInfoInput,
    { enabled: salonId > 0 }
  );
  const { data: bookingPolicy } = (trpc as any).salon.getPublicBookingPolicy.useQuery(
    salonInfoInput,
    { enabled: salonId > 0 }
  );
  const advancePaymentPercent = Math.min(100, Math.max(0, Number(bookingPolicy?.advancePaymentPercent ?? 100)));
  const advancePaymentAmount = bookingPolicy?.requireAdvancePayment ? totalPrice * advancePaymentPercent / 100 : 0;
  const remainingPaymentAmount = Math.max(0, totalPrice - advancePaymentAmount);
  const createPublicDepositCheckout = (trpc as any).appointments.createPublicDepositCheckout.useMutation();
  const { data: depositVerification } = (trpc as any).appointments.verifyPublicDepositCheckout.useQuery(
    { sessionId: depositSessionId },
    { enabled: depositSessionId.length > 0, refetchInterval: 2500 }
  );

  useEffect(() => {
    if (!depositVerification?.paymentConfirmed || confirmedData) return;
    const start = depositVerification.startTime ? new Date(depositVerification.startTime) : null;
    setConfirmedData({
      serviceNames: depositVerification.serviceNames ?? [],
      profName: depositVerification.professionalName ?? "",
      date: start ? `${format(start, "dd/MM/yyyy", { locale: ptBR })} às ${format(start, "HH:mm")}` : "Horário confirmado",
      phone: "",
      salonPhone: salonInfo?.phone ?? undefined,
      totalPrice: Number(depositVerification.totalPrice ?? 0),
      totalDuration: Number(depositVerification.totalDuration ?? 0),
      advancePaymentAmount: Number(depositVerification.advanceAmount ?? 0),
      advancePaymentPercent: Number(depositVerification.advancePercent ?? 100),
    });
    setStep("success");
  }, [depositVerification, confirmedData, salonInfo?.phone]);

  // Horários de funcionamento e exceções
  const { data: publicSchedule } = (trpc as any).salon.getPublicSchedule.useQuery(
    publicScheduleInput,
    { enabled: salonId > 0 }
  );

  // Verificar se hoje ou esta semana tem exceção
  const upcomingExceptions = useMemo(() => {
    if (!publicSchedule?.exceptions) return [];
    const now = new Date();
    const in14days = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    return publicSchedule.exceptions.filter((e: any) => {
      const d = new Date(e.exceptionDate + "T12:00:00");
      return d >= now && d <= in14days;
    });
  }, [publicSchedule]);

  const createAppointment = trpc.appointments.createPublic.useMutation();

  // Pacotes de serviços
  const packagesInput = useMemo(() => ({ salonId }), [salonId]);
  const { data: packages = [] } = (trpc as any).packages.listPublic.useQuery(
    packagesInput,
    { enabled: salonId > 0 }
  );
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [serviceTab, setServiceTab] = useState<"packages" | "individual">("packages");

  // Quando seleciona um pacote, preenche os serviços automaticamente
  function selectPackage(pkg: any) {
    if (selectedPackageId === pkg.id) {
      // Desselecionar pacote
      setSelectedPackageId(null);
      setSelectedServices([]);
    } else {
      setSelectedPackageId(pkg.id);
      setSelectedServices(pkg.items.map((i: any) => i.serviceId));
    }
  }

  const selectedServicesData = useMemo(
    () => services?.filter((s) => selectedServices.includes(s.id)) ?? [],
    [services, selectedServices]
  );
  const selectedProfData = (professionals as any[])?.find((p: any) => p.id === selectedProfessional);

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const steps: { key: Step; label: string; icon: React.ReactNode }[] = [
    { key: "service", label: "Serviços", icon: <Scissors className="w-4 h-4" /> },
    ...(serviceBranches && serviceBranches.length > 1 && !effectiveBranchId
      ? [{ key: "branch" as Step, label: "Unidade", icon: <MapPin className="w-4 h-4" /> }]
      : []),
    { key: "professional", label: "Profissional", icon: <User className="w-4 h-4" /> },
    { key: "datetime", label: "Data & Hora", icon: <Calendar className="w-4 h-4" /> },
    { key: "client", label: "Seus Dados", icon: <Phone className="w-4 h-4" /> },
    { key: "confirm", label: "Confirmar", icon: <CheckCircle className="w-4 h-4" /> },
  ];

  const stepIndex = steps.findIndex((s) => s.key === step);

  const isClientDataValid = clientName.trim().length >= 2 && clientPhone.trim().length >= 8;

  // Toggle de seleção de serviço
  function toggleService(serviceId: number) {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  }

  // Criar agendamentos em sequência (um por serviço, horários consecutivos)
  const handleConfirm = async () => {
    if (!selectedSlot || selectedServices.length === 0 || !selectedProfessional || !salonId) return;
    if (bookingPolicy?.requireAdvancePayment) {
      if (!acceptedBookingPolicy) {
        toast.error("Leia e aceite a política de reserva para continuar.");
        return;
      }
      const [slotH, slotM] = selectedSlot.split(":").map(Number);
      const startTime = new Date(Date.UTC(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        0, 0, 0, 0
      ) + ((slotH ?? 0) * 60 + (slotM ?? 0)) * 60000);
      try {
        const result = await createPublicDepositCheckout.mutateAsync({
          salonId,
          professionalId: selectedProfessional,
          serviceIds: selectedServices,
          startTime,
          notes: notes || undefined,
          clientName: clientName.trim(),
          clientPhone: clientPhone.trim(),
          clientEmail: clientEmail.trim() || undefined,
          branchId: effectiveBranchId ?? undefined,
          origin: window.location.origin,
        });
        window.location.assign(result.checkoutUrl);
        return;
      } catch (error: any) {
        toast.error(error?.message ?? "Não foi possível iniciar o pagamento do sinal.");
        return;
      }
    }
    // Agendamento público: não exige login — nome e telefone já foram preenchidos no passo "Seus Dados"
    setCreatingProgress(0);
    // Construir timestamp UTC a partir da data selecionada + string de hora (HH:mm)
    // selectedDate é local; selectedSlot é "HH:mm" no horário do salão (BRT)
    // Usamos UTC midnight do dia local + minutos para evitar drift de fuso
    const [slotH, slotM] = selectedSlot.split(":").map(Number);
    const dayUTC = new Date(Date.UTC(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0, 0, 0, 0
    ));
    let currentStart = new Date(dayUTC.getTime() + (slotH! * 60 + slotM!) * 60000);

    for (let i = 0; i < selectedServicesData.length; i++) {
      const svc = selectedServicesData[i];
      setCreatingProgress(i + 1);
      try {
        await createAppointment.mutateAsync({
          salonId,
          professionalId: selectedProfessional,
          serviceId: svc.id,
          startTime: new Date(currentStart),
          notes: i === 0 ? notes : undefined,
          clientName: clientName.trim() || "Cliente",
          clientPhone: clientPhone.trim() || "00000000000",
          clientEmail: clientEmail.trim() || undefined,
          branchId: effectiveBranchId ?? undefined,
        });
        // Próximo serviço começa após o fim deste
        currentStart = new Date(currentStart.getTime() + svc.durationMinutes * 60000);
      } catch (e: any) {
        toast.error(`Erro ao agendar "${svc.name}": ${e.message}`);
        setCreatingProgress(0);
        return;
      }
    }

    // Sucesso — guardar dados para tela de sucesso
    setConfirmedData({
      serviceNames: selectedServicesData.map((s) => s.name),
      profName: selectedProfData?.name ?? "",
      date: `${format(selectedDate, "dd/MM/yyyy", { locale: ptBR })} às ${selectedSlot}`,
      phone: clientPhone.trim(),
      salonPhone: salonInfo?.phone ?? undefined,
      totalPrice,
      totalDuration: totalDurationMinutes,
    });
    setCreatingProgress(0);
    setStep("success");
  };

  // Sem salonId válido na URL
  if (!salonId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Scissors className="w-12 h-12 text-muted-foreground mx-auto" />
          <p className="text-muted-foreground">Salão não especificado. Acesse pelo link do salão.</p>
          <Link href="/">
            <Button variant="outline">Voltar ao início</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-border/50 shadow-sm">
        <div className="container flex items-center justify-between h-16">
          <button onClick={() => window.history.back()} className="flex items-center gap-2 cursor-pointer">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Voltar</span>
          </button>
          <h1 className="font-[Cormorant_Garamond,serif] text-xl font-semibold">
            {effectiveBranchName ? `Agendamento — ${effectiveBranchName}` : "Novo Agendamento"}
          </h1>
          <div />
        </div>
      </nav>

      <div className="container pt-24 pb-16 max-w-3xl">
        {/* Steps indicator */}
        <div className="mb-8 rounded-2xl border bg-card/70 p-3 shadow-sm">
          <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>Etapa {Math.min(stepIndex + 1, steps.length)} de {steps.length}</span>
            <span>{steps[stepIndex]?.label ?? "Confirmação"}</span>
          </div>
          <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-muted" aria-label={`Progresso: etapa ${Math.min(stepIndex + 1, steps.length)} de ${steps.length}`}>
            <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${Math.max(12, ((stepIndex + 1) / steps.length) * 100)}%` }} />
          </div>
          <div className="flex items-center justify-center gap-1 flex-wrap">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center gap-1">
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                  i === stepIndex
                    ? "bg-primary text-primary-foreground"
                    : i < stepIndex
                    ? "bg-primary/20 text-primary"
                    : "bg-secondary text-muted-foreground"
                }`}
              >
                {s.icon}
                <span className="hidden sm:block">{s.label}</span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-4 h-px ${i < stepIndex ? "bg-primary" : "bg-border"}`} />
              )}
            </div>
          ))}
          </div>
        </div>

        {/* Avisos de pagamento e exceções próximas */}
        {depositCancelled && step !== "success" && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300/50 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>O pagamento do sinal não foi concluído. Revise os dados e tente novamente; o horário fica reservado apenas por tempo limitado.</span>
          </div>
        )}
        {upcomingExceptions.length > 0 && (
          <div className="mb-6 space-y-2">
            {upcomingExceptions.map((exc: any) => (
              <div
                key={exc.id}
                className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
                  exc.isClosed
                    ? "border-red-300/50 bg-red-50/10 text-red-700 dark:text-red-400"
                    : "border-amber-300/50 bg-amber-50/10 text-amber-700 dark:text-amber-400"
                }`}
              >
                {exc.isClosed ? (
                  <CalendarX className="w-4 h-4 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                )}
                <span>
                  <strong>
                    {new Date(exc.exceptionDate + "T12:00:00").toLocaleDateString("pt-BR", {
                      weekday: "long",
                      day: "2-digit",
                      month: "long",
                    })}
                  </strong>
                  {exc.isClosed
                    ? ` — Salão fechado${exc.reason ? ` (${exc.reason})` : ""}`
                    : ` — Horário especial: ${exc.openTime ?? ""} às ${exc.closeTime ?? ""}${exc.reason ? ` (${exc.reason})` : ""}`}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Banner de Promoções Ativas */}
        {step === "service" && <PromotionsBanner salonId={salonId} />}

        {/* Horários de Funcionamento */}
        {publicSchedule?.workingHours && publicSchedule.workingHours.length > 0 && step === "service" && (
          <details className="mb-6 group">
            <summary className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground select-none list-none">
              <Clock className="w-4 h-4" />
              <span>Ver horários de atendimento</span>
              <ChevronRight className="w-3.5 h-3.5 ml-auto transition-transform group-open:rotate-90" />
            </summary>
            <div className="mt-3 rounded-lg border border-border/50 bg-secondary/10 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day, idx) => {
                  const wh = publicSchedule.workingHours.find((h: any) => h.dayOfWeek === idx);
                  return (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className={`font-medium w-8 ${!wh?.isOpen ? "text-muted-foreground" : "text-foreground"}`}>{day}</span>
                      {wh?.isOpen ? (
                        <span className="text-muted-foreground">{wh.openTime ?? "08:00"} – {wh.closeTime ?? "18:00"}</span>
                      ) : (
                        <span className="text-muted-foreground/50">Fechado</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </details>
        )}

        {/* Step: Service (multi-seleção) */}
        {step === "service" && (
          <div>
            <h2 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold mb-1">Escolha os Serviços</h2>
            <p className="text-sm text-muted-foreground mb-4">Selecione um pacote ou serviços individuais.</p>

            {/* Abas: Pacotes / Serviços Individuais */}
            {(packages as any[]).length > 0 && (
              <div className="flex gap-2 mb-5">
                <button
                  onClick={() => setServiceTab("packages")}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                    serviceTab === "packages"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  ✨ Pacotes ({(packages as any[]).length})
                </button>
                <button
                  onClick={() => { setServiceTab("individual"); setSelectedPackageId(null); setSelectedServices([]); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                    serviceTab === "individual"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}
                >
                  Serviços Individuais
                </button>
              </div>
            )}

            {/* Pacotes */}
            {serviceTab === "packages" && (packages as any[]).length > 0 && (
              <div className="grid gap-3 mb-4">
                {(packages as any[]).map((pkg: any) => {
                  const origTotal = pkg.items.reduce((s: number, i: any) => s + parseFloat(i.servicePrice ?? "0"), 0);
                  const disc = parseFloat(pkg.discountValue ?? "0");
                  const finalPrice = pkg.discountType === "percent"
                    ? Math.max(0, origTotal - (origTotal * disc) / 100)
                    : Math.max(0, origTotal - disc);
                  const duration = pkg.items.reduce((s: number, i: any) => s + (i.serviceDuration ?? 0), 0);
                  const saved = origTotal - finalPrice;
                  const isSelected = selectedPackageId === pkg.id;
                  return (
                    <Card
                      key={pkg.id}
                      className={`cursor-pointer transition-all hover:border-primary/40 hover:shadow-md overflow-hidden ${
                        isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border/50"
                      }`}
                      onClick={() => selectPackage(pkg)}
                    >
                      {pkg.imageUrl && (
                        <div className="h-28 w-full overflow-hidden">
                          <img src={pkg.imageUrl} alt={pkg.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            {/* Checkbox visual */}
                            <div
                              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                                isSelected ? "bg-primary border-primary" : "border-border bg-background"
                              }`}
                            >
                              {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-foreground">{pkg.name}</p>
                                <Badge variant="secondary" className="text-xs shrink-0">✨ Pacote</Badge>
                              </div>
                              {pkg.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{pkg.description}</p>
                              )}
                              <div className="flex flex-wrap gap-1 mt-2">
                                {pkg.items.map((item: any) => (
                                  <span key={item.serviceId} className="text-xs bg-muted rounded-full px-2 py-0.5">
                                    {item.serviceName}
                                  </span>
                                ))}
                              </div>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{duration} min</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            {saved > 0 && (
                              <p className="text-xs text-muted-foreground line-through">
                                R$ {origTotal.toFixed(2).replace(".", ",")}
                              </p>
                            )}
                            <p className="text-lg font-bold text-primary">
                              R$ {finalPrice.toFixed(2).replace(".", ",")}
                            </p>
                            {saved > 0 && (
                              <p className="text-xs text-green-600 font-medium">
                                Economia R$ {saved.toFixed(2).replace(".", ",")}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Serviços Individuais (aba individual ou quando não há pacotes) */}
            {(serviceTab === "individual" || (packages as any[]).length === 0) && (
              <div className="grid gap-3">
                {servicesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : !services || services.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">Nenhum serviço disponível.</p>
                ) : (
                  <>
                {services.map((service) => {
                  const isSelected = selectedServices.includes(service.id);
                  return (
                    <Card
                      key={service.id}
                      className={`cursor-pointer transition-all hover:border-primary/40 hover:shadow-md overflow-hidden ${
                        isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/30" : "border-border/50"
                      }`}
                      onClick={() => toggleService(service.id)}
                    >
                      {(service as any).imageUrl && (
                        <div className="h-24 w-full overflow-hidden">
                          <img src={(service as any).imageUrl} alt={service.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Checkbox visual */}
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                              isSelected
                                ? "bg-primary border-primary"
                                : "border-border bg-background"
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                          </div>
                          {!(service as any).imageUrl && (
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Scissors className="w-4 h-4 text-primary" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">{service.name}</p>
                            {(service as any).description && (
                              <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{(service as any).description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                              <span className="text-xs text-muted-foreground">{service.durationMinutes} min</span>
                              {service.category && (
                                <Badge variant="secondary" className="text-xs">{service.category}</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-lg font-semibold text-primary">
                            R$ {Number(service.price).toFixed(2).replace(".", ",")}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                    })}
                  </>
                )}
              </div>
            )}

            {/* Resumo dos serviços selecionados */}
            {selectedServices.length > 0 && (
              <div className="mt-4 rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
                <p className="text-sm font-semibold text-foreground">
                  {selectedServices.length === 1 ? "1 serviço selecionado" : `${selectedServices.length} serviços selecionados`}
                </p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> Duração total
                  </span>
                  <span className="font-medium">{totalDurationMinutes} min</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Valor total</span>
                  <span className="font-semibold text-primary text-base">
                    R$ {totalPrice.toFixed(2).replace(".", ",")}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => {
                  // Se o serviço principal tem múltiplas filiais e nenhuma foi pré-selecionada, ir para step de filial
                  if (serviceBranches && serviceBranches.length > 1 && !effectiveBranchId) {
                    setStep("branch");
                  } else {
                    setStep("professional");
                  }
                }}
                disabled={selectedServices.length === 0}
                className="bg-primary text-primary-foreground"
              >
                Próximo <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step: Branch (seleção de filial) */}
        {step === "branch" && (
          <div>
            <button onClick={() => setStep("service")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold mb-2">Escolha a Unidade</h2>
            <p className="text-sm text-muted-foreground mb-6">Este serviço está disponível em mais de uma unidade. Selecione onde deseja ser atendido.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(serviceBranches ?? []).map((branch: any) => (
                <Card
                  key={branch.branchId}
                  className={`cursor-pointer transition-all hover:border-primary/40 hover:shadow-md ${
                    selectedBranchId === branch.branchId ? "border-primary bg-primary/5" : "border-border/50"
                  }`}
                  onClick={() => {
                    setSelectedBranchId(branch.branchId);
                    setSelectedBranchName(branch.branchName);
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{branch.branchName}</p>
                        {branch.branchAddress && (
                          <p className="text-xs text-muted-foreground mt-0.5">{branch.branchAddress}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setStep("professional")}
                disabled={!selectedBranchId}
                className="bg-primary text-primary-foreground"
              >
                Próximo <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step: Professional */}
        {step === "professional" && (
          <div>
            <button
              onClick={() => {
                if (serviceBranches && serviceBranches.length > 1 && !branchId) {
                  setStep("branch");
                } else {
                  setStep("service");
                }
              }}
              className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground"
            >
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold mb-6">Escolha o Profissional</h2>
            {profLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : !professionals || professionals.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Nenhum profissional disponível.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(professionals as any[]).map((prof: any) => (
                  <Card
                    key={prof.id}
                    className={`cursor-pointer transition-all hover:border-primary/40 hover:shadow-md ${
                      selectedProfessional === prof.id ? "border-primary bg-primary/5" : "border-border/50"
                    }`}
                    onClick={() => setSelectedProfessional(prof.id)}
                  >
                    <CardContent className="p-4 flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg shrink-0"
                        style={{ backgroundColor: prof.color ?? "#C9A96E" }}
                      >
                        {prof.avatarUrl ? (
                          <img src={prof.avatarUrl} alt={prof.name} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          prof.name.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-foreground">{prof.name}</p>
                          {prof.allowOverlap && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200 font-medium">
                              Aceita simultâneos
                            </span>
                          )}
                        </div>
                        {prof.bio && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{prof.bio}</p>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setStep("datetime")}
                disabled={!selectedProfessional}
                className="bg-primary text-primary-foreground"
              >
                Próximo <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step: Date & Time */}
        {step === "datetime" && (
          <div>
            <button onClick={() => setStep("professional")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold mb-2">Escolha Data & Horário</h2>
            {totalDurationMinutes > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                Duração total: <span className="font-medium text-foreground">{totalDurationMinutes} min</span>
                {selectedServices.length > 1 && (
                  <span className="text-xs ml-1">({selectedServices.length} serviços em sequência)</span>
                )}
              </p>
            )}

            {/* Week selector */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setWeekStart((d) => addDays(d, -7))}
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium text-foreground">
                {format(weekStart, "MMMM yyyy", { locale: ptBR })}
              </span>
              <button
                onClick={() => setWeekStart((d) => addDays(d, 7))}
                className="p-1.5 rounded-lg hover:bg-secondary transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-6">
              {weekDays.map((day) => {
                const past = isPast(day) && !isToday(day);
                return (
                  <button
                    key={day.toISOString()}
                    disabled={past}
                    onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                    className={`flex flex-col items-center p-2 rounded-xl text-xs transition-all ${
                      isSameDay(day, selectedDate)
                        ? "bg-primary text-primary-foreground"
                        : past
                        ? "opacity-30 cursor-not-allowed"
                        : isToday(day)
                        ? "bg-primary/10 text-primary font-semibold"
                        : "hover:bg-secondary"
                    }`}
                  >
                    <span className="text-[10px] uppercase">{format(day, "EEE", { locale: ptBR })}</span>
                    <span className="text-base font-semibold mt-0.5">{format(day, "d")}</span>
                  </button>
                );
              })}
            </div>

            {/* Time slots */}
            <div>
              <p className="text-sm font-medium text-foreground mb-3">
                Horários disponíveis — {format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
              </p>
              {slotsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : !slots || slots.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  Nenhum horário disponível neste dia.
                </p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {slots.map((slot) => (
                    <button
                      key={slot.timeStr}
                      disabled={!slot.available}
                      onClick={() => setSelectedSlot(slot.timeStr)}
                      className={`py-2 px-1 rounded-lg text-xs font-medium transition-all ${
                        selectedSlot === slot.timeStr
                          ? "bg-primary text-primary-foreground"
                          : !slot.available
                          ? "bg-secondary/50 text-muted-foreground/50 cursor-not-allowed line-through"
                          : "bg-secondary hover:bg-primary/10 hover:text-primary text-foreground"
                      }`}
                    >
                      {slot.timeStr}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setStep("client")}
                disabled={!selectedSlot}
                className="bg-primary text-primary-foreground"
              >
                Próximo <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step: Client Data */}
        {step === "client" && (
          <div>
            <button onClick={() => setStep("datetime")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold mb-2">Seus Dados</h2>
            <p className="text-sm text-muted-foreground mb-6">Preencha seus dados para confirmar o agendamento.</p>

            <Card className="border-border/50 mb-6">
              <CardContent className="p-6 space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="clientName" className="text-sm font-medium flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Nome completo <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="clientName"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Seu nome completo"
                    className="border-border/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientPhone" className="text-sm font-medium flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Telefone / WhatsApp <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="clientPhone"
                    value={clientPhone}
                    onChange={(e) => setClientPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                    type="tel"
                    className="border-border/50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientEmail" className="text-sm font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    E-mail <span className="text-muted-foreground text-xs">(opcional)</span>
                  </Label>
                  <Input
                    id="clientEmail"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    placeholder="seu@email.com"
                    type="email"
                    className="border-border/50"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setStep("confirm")}
                disabled={!isClientDataValid}
                className="bg-primary text-primary-foreground"
              >
                Próximo <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === "success" && confirmedData && (
          <div className="py-8 space-y-6">
            {/* Hero de sucesso */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
                    <CalendarCheck className="w-12 h-12 text-green-600" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-2 border-background">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
              <div>
                <h2 className="text-3xl font-[Cormorant_Garamond,serif] font-semibold text-foreground">
                  {confirmedData.serviceNames.length > 1 ? "Agendamentos Confirmados!" : "Agendamento Confirmado!"}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {confirmedData.serviceNames.length > 1
                    ? `${confirmedData.serviceNames.length} serviços agendados com sucesso em sequência.`
                    : "Seu agendamento foi registrado com sucesso."}
                </p>
              </div>
            </div>

            {/* Card de resumo completo */}
            <Card className="border-green-200/60 bg-green-50/30">
              <CardContent className="p-5 space-y-0">
                {/* Data e hora */}
                <div className="flex items-center gap-3 py-3 border-b border-border/40">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <Calendar className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Data e horário</p>
                    <p className="text-sm font-semibold text-foreground">{confirmedData.date}</p>
                    {confirmedData.totalDuration > 0 && (
                      <p className="text-xs text-muted-foreground">{confirmedData.totalDuration} min no total</p>
                    )}
                  </div>
                </div>

                {/* Profissional */}
                <div className="flex items-center gap-3 py-3 border-b border-border/40">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Profissional</p>
                    <p className="text-sm font-semibold text-foreground">{confirmedData.profName}</p>
                  </div>
                </div>

                {/* Lista de serviços */}
                <div className="py-3 border-b border-border/40">
                  <p className="text-xs text-muted-foreground mb-2">
                    {confirmedData.serviceNames.length > 1 ? "Serviços" : "Serviço"}
                  </p>
                  <div className="space-y-1.5">
                    {confirmedData.serviceNames.map((name, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-green-500 shrink-0" />
                        <span className="text-sm font-medium text-foreground">{name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Valor total */}
                <div className="flex items-center justify-between pt-3">
                  <span className="text-sm text-muted-foreground">Valor total</span>
                  <span className="text-xl font-bold text-primary">
                    R$ {confirmedData.totalPrice.toFixed(2).replace(".", ",")}
                  </span>
                </div>
                {confirmedData.advancePaymentAmount && confirmedData.advancePaymentAmount > 0 && (
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-green-100/70 px-3 py-2 text-sm">
                    <span className="font-medium text-green-800">{confirmedData.advancePaymentPercent === 100 ? "Pagamento recebido" : "Sinal recebido"}</span>
                    <span className="font-semibold text-green-800">R$ {confirmedData.advancePaymentAmount.toFixed(2).replace(".", ",")}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ações */}
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  if (!downloadBookingCalendar(confirmedData)) toast.error("Não foi possível gerar o evento do calendário.");
                }}
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Adicionar ao calendário
              </Button>

              {salonId > 0 && <Button asChild variant="outline" className="w-full">
                <a href={`/api/contract/download?salonId=${salonId}`}>
                  <Download className="w-4 h-4 mr-2" />
                  Baixar contrato de prestação de serviços
                </a>
              </Button>}

              {/* Botão WhatsApp */}
              {confirmedData.salonPhone && (() => {
                const phone = confirmedData.salonPhone!.replace(/\D/g, "");
                const countryPhone = phone.startsWith("55") ? phone : `55${phone}`;
                const servicesText = confirmedData.serviceNames.map((n) => `• ${n}`).join("\n");
                const msg = encodeURIComponent(
                  `Olá! Acabei de fazer um agendamento:\n${servicesText}\n• Profissional: ${confirmedData.profName}\n• Início: ${confirmedData.date}\n• Duração: ${confirmedData.totalDuration} min\n\nAguardo a confirmação! 😊`
                );
                return (
                  <a
                    href={`https://wa.me/${countryPhone}?text=${msg}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3.5 px-6 rounded-xl transition-colors text-sm shadow-sm"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Confirmar pelo WhatsApp
                  </a>
                );
              })()}

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setStep("service");
                  setSelectedServices([]);
                  setSelectedProfessional(null);
                  setSelectedSlot(null);
                  setNotes("");
                  setClientName(user?.name ?? "");
                  setClientPhone(user?.phone ?? "");
                  setClientEmail(user?.email ?? "");
                  setConfirmedData(null);
                }}
              >
                <CalendarCheck className="w-4 h-4 mr-2" />
                Fazer outro agendamento
              </Button>
            </div>
          </div>
        )}

        {/* Step: Confirm */}
        {step === "confirm" && (
          <div>
            <button onClick={() => setStep("client")} className="flex items-center gap-1 text-sm text-muted-foreground mb-4 hover:text-foreground">
              <ChevronLeft className="w-4 h-4" /> Voltar
            </button>
            <h2 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold mb-1">Confirmar Agendamento</h2>
            <p className="text-sm text-muted-foreground mb-6">Revise os detalhes antes de confirmar.</p>

            {/* Profissional selecionado — destaque visual */}
            {selectedProfData && (
              <div className="flex items-center gap-4 rounded-xl border border-border/50 bg-secondary/20 p-4 mb-4">
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl shrink-0 overflow-hidden"
                  style={{ backgroundColor: selectedProfData.color ?? "#C9A96E" }}
                >
                  {selectedProfData.avatarUrl ? (
                    <img src={selectedProfData.avatarUrl} alt={selectedProfData.name} className="w-full h-full object-cover" />
                  ) : (
                    selectedProfData.name.charAt(0).toUpperCase()
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Profissional</p>
                  <p className="font-semibold text-foreground text-lg">{selectedProfData.name}</p>
                  {selectedProfData.bio && (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{selectedProfData.bio}</p>
                  )}
                </div>
              </div>
            )}

            {/* Data e hora — destaque */}
            <div className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Data e horário</p>
                <p className="font-semibold text-foreground">
                  {selectedSlot && format(selectedDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </p>
                <p className="text-sm text-primary font-medium">às {selectedSlot} · {totalDurationMinutes} min no total</p>
              </div>
            </div>

            {/* Serviços selecionados com preços individuais */}
            <Card className="border-border/50 mb-4">
              <CardContent className="p-0">
                <div className="px-4 pt-4 pb-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {selectedServicesData.length > 1 ? `${selectedServicesData.length} Serviços` : "Serviço"}
                  </p>
                </div>
                {selectedServicesData.map((svc, idx) => {
                  let startMin = 0;
                  for (let k = 0; k < idx; k++) startMin += selectedServicesData[k]!.durationMinutes;
                  const [slotH2, slotM2] = (selectedSlot ?? "00:00").split(":").map(Number);
                  const totalMin = (slotH2 ?? 0) * 60 + (slotM2 ?? 0) + startMin;
                  const hh = String(Math.floor(totalMin / 60) % 24).padStart(2, "0");
                  const mm = String(totalMin % 60).padStart(2, "0");
                  return (
                    <div key={svc.id} className={`flex items-center gap-3 px-4 py-3 ${idx < selectedServicesData.length - 1 ? "border-b border-border/40" : "pb-4"}` }>
                      {(svc as any).imageUrl ? (
                        <img src={(svc as any).imageUrl} alt={svc.name} className="w-10 h-10 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <Scissors className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{svc.name}</p>
                        <p className="text-xs text-muted-foreground">{hh}:{mm} · {svc.durationMinutes} min</p>
                      </div>
                      <p className="text-sm font-semibold text-foreground shrink-0">
                        R$ {Number(svc.price).toFixed(2).replace(".", ",")}
                      </p>
                    </div>
                  );
                })}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border/50 bg-muted/20 rounded-b-xl">
                  <span className="text-sm font-semibold text-foreground">Total</span>
                  <span className="text-xl font-bold text-primary">R$ {totalPrice.toFixed(2).replace(".", ",")}</span>
                </div>
              </CardContent>
            </Card>

            {/* Dados do cliente — resumo compacto */}
            <Card className="border-border/50 mb-4">
              <CardContent className="p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Seus Dados</p>
                <div className="flex items-center gap-2 text-sm">
                  <User className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="font-medium text-foreground">{clientName}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <span className="text-foreground">{clientPhone}</span>
                </div>
                {clientEmail && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="text-foreground">{clientEmail}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="mb-6">
              <Label className="text-sm font-medium text-foreground mb-2 block">
                Observações (opcional)
              </Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Alguma preferência ou pedido especial para o profissional..."
                className="resize-none border-border/50"
                rows={3}
              />
            </div>

            {bookingPolicy && (
              <Card className="mb-4 border-primary/20 bg-primary/[0.03]">
                <CardContent className="space-y-3 p-4">
                  {bookingPolicy.requireAdvancePayment && (
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Sinal para confirmar o horário</p>
                        <p className="mt-1 text-xs text-muted-foreground">Será cobrado {advancePaymentPercent}% do valor total em pagamento seguro.</p>
                        {remainingPaymentAmount > 0 && <p className="mt-1 text-xs font-medium text-foreground">Saldo após o sinal: R$ {remainingPaymentAmount.toFixed(2).replace(".", ",")}</p>}
                      </div>
                      <Badge className="shrink-0">R$ {advancePaymentAmount.toFixed(2).replace(".", ",")}</Badge>
                    </div>
                  )}
                  <div className={bookingPolicy.requireAdvancePayment ? "border-t border-border/50 pt-3" : ""}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Política de cancelamento</p>
                    <p className="mt-1 text-xs leading-relaxed text-foreground">{bookingPolicy.cancellationPolicy}</p>
                  </div>
                  <label className="flex cursor-pointer items-start gap-2 text-xs text-foreground">
                    <input type="checkbox" checked={acceptedBookingPolicy} onChange={(e) => setAcceptedBookingPolicy(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border accent-primary" />
                    <span>Li e aceito a política de reserva e cancelamento.</span>
                  </label>
                </CardContent>
              </Card>
            )}

            <Button
              onClick={handleConfirm}
              disabled={createAppointment.isPending || createPublicDepositCheckout.isPending || creatingProgress > 0 || (!!bookingPolicy?.requireAdvancePayment && !acceptedBookingPolicy)}
              className="w-full bg-primary text-primary-foreground"
              size="lg"
            >
              {(createAppointment.isPending || createPublicDepositCheckout.isPending || creatingProgress > 0) ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {selectedServicesData.length > 1
                    ? `Agendando ${creatingProgress} de ${selectedServicesData.length}...`
                    : "Confirmando..."}
                </>
              ) : (
                bookingPolicy?.requireAdvancePayment
                  ? <><CreditCard className="w-4 h-4 mr-2" /> Ir para pagamento seguro</>
                  : <><CheckCircle className="w-4 h-4 mr-2" /> Confirmar Agendamento</>
              )}
            </Button>
          </div>
        )}
      </div>

      <BeautyChatBot salonId={salonId} />
    </div>
  );
}

// ─── Componente de Banner de Promoções ──────────────────────────────────────

function PromotionsBanner({ salonId }: { salonId: number }) {
  const [expanded, setExpanded] = useState(false);
  const { data: promotions = [] } = (trpc as any).promotions.listActivePublic.useQuery(
    { salonId },
    { enabled: salonId > 0 }
  );

  if (!promotions || promotions.length === 0) return null;

  return (
    <div className="mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between gap-3 rounded-xl border border-orange-300/60 bg-gradient-to-r from-orange-50/80 to-amber-50/80 dark:from-orange-950/30 dark:to-amber-950/30 px-4 py-3 text-left hover:border-orange-400/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-orange-500 shrink-0" />
          <span className="text-sm font-semibold text-orange-700 dark:text-orange-400">
            {promotions.length === 1
              ? "1 promoção ativa!"
              : `${promotions.length} promoções ativas!`}
          </span>
          <span className="text-xs text-orange-600/70 dark:text-orange-400/70 hidden sm:block">
            Confira nossas ofertas especiais
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-orange-500 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {promotions.map((p: any) => (
            <div
              key={p.id}
              className="rounded-lg border border-orange-200/60 bg-orange-50/60 dark:bg-orange-950/20 dark:border-orange-800/40 p-4"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-orange-800 dark:text-orange-300">{p.title}</p>
                  <p className="text-sm text-orange-700/80 dark:text-orange-400/80 mt-1">{p.description}</p>
                  {p.conditions && (
                    <p className="text-xs text-orange-600/70 dark:text-orange-400/60 mt-1 italic">⚠ {p.conditions}</p>
                  )}
                  <p className="text-xs text-orange-500/70 mt-1">
                    Válida até {new Date(p.endsAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })}
                  </p>
                </div>
                {p.discountText && (
                  <div className="shrink-0">
                    <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 text-white text-xs font-bold px-3 py-1">
                      <Tag className="w-3 h-3" />
                      {p.discountText}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
